import { useLingui } from '@lingui/react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { SlotChildPresence } from '@/types/coverage';
import {
  OutsideBookingIcon,
  PresenceSourceBadge,
  useOutsideBookingReason,
  useDriftReason,
} from './ChildPresenceRow';

interface ArrivalsPopoverProps {
  groupName: string;
  presences: SlotChildPresence[];
}

const byArrival = (a: SlotChildPresence, b: SlotChildPresence) =>
  a.arrival.localeCompare(b.arrival) || a.display_name.localeCompare(b.display_name);

/**
 * "Arrivals and departures" of one group for the planned day: the forecast times of
 * every child, sorted by arrival, with the source of each time.
 */
export function ArrivalsPopover({ groupName, presences }: ArrivalsPopoverProps) {
  const { i18n } = useLingui();
  const outsideBookingReason = useOutsideBookingReason();
  const driftReason = useDriftReason();
  const sorted = [...presences].sort(byArrival);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-5 gap-1 px-1 text-xs font-normal text-muted-foreground"
          onClick={(e) => e.stopPropagation()}
          title={i18n._({
            id: 'shifts.arrivals.trigger.title',
            message: '{count, plural, one {# child expected. Click for arrivals and departures.} other {# children expected. Click for arrivals and departures.}}',
            values: { count: sorted.length },
          })}
          aria-label={i18n._({
            id: 'shifts.arrivals.trigger.ariaLabel',
            message: 'Arrivals and departures of {group}',
            values: { group: groupName },
          })}
        >
          <Clock className="h-3 w-3" />
          {sorted.length}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" onClick={(e) => e.stopPropagation()}>
        <div className="border-b px-3 py-2">
          <div className="text-sm font-semibold">
            {i18n._({ id: 'shifts.arrivals.title', message: 'Arrivals and departures' })}
          </div>
          <div className="text-xs text-muted-foreground">{groupName}</div>
        </div>
        {/* Where each time comes from (#2038 UX pass) */}
        <p className="border-b px-3 py-2 text-xs text-muted-foreground" data-testid="arrivals-sources">
          {i18n._({
            id: 'shifts.arrivals.sources',
            message:
              'Booking: the times of the booked service. Expected: the drop-off and pick-up times the parents reported. History: the usual times from the check-ins of the last weeks.',
          })}
        </p>
        {sorted.length === 0 ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">
            {i18n._({ id: 'shifts.arrivals.empty', message: 'No children expected on this day.' })}
          </div>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto" data-testid="arrivals-list">
            {sorted.map((child) => {
              const reason = outsideBookingReason(child);
              const drift = driftReason(child);
              return (
                <li key={child.child_id} className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 px-3 py-2 text-sm">
                  <div className="flex min-w-0 items-center gap-1">
                    <span className="truncate font-medium">{child.display_name}</span>
                    {reason && <OutsideBookingIcon reason={reason} />}
                    {drift && <OutsideBookingIcon reason={drift} />}
                  </div>
                  <div className="text-right tabular-nums">
                    {child.arrival}-{child.departure}
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      {i18n._({ id: 'shifts.arrivals.arrival', message: 'Arrival' })}
                      <PresenceSourceBadge
                        source={child.arrival_source}
                        samples={child.arrival_samples}
                      />
                    </span>
                    <span className="flex items-center gap-1">
                      {i18n._({ id: 'shifts.arrivals.departure', message: 'Departure' })}
                      <PresenceSourceBadge
                        source={child.departure_source}
                        samples={child.departure_samples}
                      />
                    </span>
                    {drift && <span className="text-warning-on-surface">{drift}</span>}
                    {child.booked_from && child.booked_to && (
                      <span>
                        {i18n._({
                          id: 'shifts.arrivals.booked',
                          message: 'Booked {from}-{to}',
                          values: { from: child.booked_from, to: child.booked_to },
                        })}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
